import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContextBackend';
import { setAccessToken as setStoredAccessToken, setRefreshToken as setStoredRefreshToken } from '../utils/tokenStorage';
import { Shield, CheckCircle } from '@phosphor-icons/react';
import { Button, Input } from '@t-one-internal/design-system';
import {
  initiateTwoFactorSetup,
  completeTwoFactorSetup,
  initiatePublicTwoFactorSetup,
  completePublicTwoFactorSetup
} from '../services/twoFactorApi';
import { QRCodeSVG } from 'qrcode.react';

interface SetupData {
  secret: string;
  qrCode: string;
}

interface LoginCredentials {
  unique_id: string;
  password: string;
  verification_id?: string;
}

type Mode = 'setup' | 'verification';

export default function TwoFactorSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loginWithCredentials } = useAuth();

  // Determine mode from location state
  const mode: Mode = (location.state as any)?.mode === 'verification' ? 'verification' : 'setup';
  const loginCredentials: LoginCredentials | null = (location.state as any)?.loginCredentials || null;

  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [code, setCode] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState<'loading' | 'qr' | 'verification' | 'complete'>(
    mode === 'verification' ? 'verification' : 'loading'
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [otpMethod, setOtpMethod] = useState<'totp' | 'email'>('totp');
  const [userEmail, setUserEmail] = useState<string>('');

  // Use ref to ensure we only call /initiate once (only for setup mode)
  const initiateCalledRef = useRef(false);

  // Check for session_id from login flow
  useEffect(() => {
    const storedSessionId = localStorage.getItem('2fa_session_id');
    if (storedSessionId) {
      console.log('🔐 Found session_id for 2FA setup:', storedSessionId);
      setSessionId(storedSessionId);
    } else {
      console.error('❌ No session_id found in localStorage! User may need to login again.');
    }
  }, []);

  // Auto-initiate setup on mount - ONLY ONCE (only for setup mode)
  useEffect(() => {
    // Skip if in verification mode
    if (mode === 'verification') {
      console.log('🔐 Verification mode - skipping /initiate');
      return;
    }

    // Prevent multiple calls (React Strict Mode, hot reload, etc.)
    if (initiateCalledRef.current) {
      console.log('⏭️ Skipping duplicate /initiate call');
      return;
    }

    initiateCalledRef.current = true;
    handleInitiateSetup();
  }, [mode]);

  const handleInitiateSetup = async () => {
    console.log('🔄 Calling /initiate endpoint (should only happen ONCE)...');
    setLoading(true);
    setError('');

    try {
      // ALWAYS get FRESH session_id from localStorage at call time (not from state)
      const freshSessionId = localStorage.getItem('2fa_session_id');
      console.log('🔐 FRESH session_id from localStorage:', freshSessionId);

      if (freshSessionId) {
        // NEW FLOW: Use public endpoint with session_id
        console.log('🔐 Using session-based 2FA setup (public endpoint)');
        console.log('🔐 Sending FULL session_id to backend:', freshSessionId);
        const response = await initiatePublicTwoFactorSetup(freshSessionId);

        if (response.success && response.data) {
          // Detect OTP method from response
          const method = response.data.otp_method || response.data.otp_preference || 'totp';
          setOtpMethod(method === 'email' ? 'email' : 'totp');
          
          if (response.data.user_email) {
            setUserEmail(response.data.user_email);
          }

          if (method === 'email') {
            // Email OTP setup - skip QR code, go directly to verification
            console.log('✅ Email OTP setup initiated');
            setStep('verification');
          } else {
            // TOTP setup - show QR code
            const setupInfo: SetupData = {
              secret: response.data.secret,
              qrCode: response.data.qr_code,
            };
            console.log('✅ Public /initiate response received (TOTP)');
            setSetupData(setupInfo);
            setStep('qr');
          }
        }
      } else {
        // OLD FLOW: Use authenticated endpoint (fallback for existing users)
        const token = localStorage.getItem('access_token');
        console.log('🔐 2FA Setup - Token check:', token ? `Token exists (${token.substring(0, 20)}...)` : 'No token found');

        if (!token) {
          setError('Authentication required. Please log in again.');
          setStep('qr');
          setLoading(false);
          return;
        }

        const response = await initiateTwoFactorSetup();

        if (response.success && response.data) {
          // Detect OTP method from response
          const method = response.data.otp_method || response.data.otp_preference || 'totp';
          setOtpMethod(method === 'email' ? 'email' : 'totp');
          
          if (response.data.user_email) {
            setUserEmail(response.data.user_email);
          }

          if (method === 'email') {
            // Email OTP setup - skip QR code, go directly to verification
            console.log('✅ Email OTP setup initiated');
            setStep('verification');
          } else {
            // TOTP setup - show QR code
            const setupInfo: SetupData = {
              secret: response.data.secret,
              qrCode: response.data.qr_code,
            };
            console.log('✅ Authenticated /initiate response received (TOTP)');
            setSetupData(setupInfo);
            setStep('qr');
          }
        }
      }
    } catch (err: any) {
      console.error('❌ 2FA initiate error:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to initiate 2FA setup';
      setError(errorMessage);
      setStep('qr'); // Show UI even on error
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (mode === 'verification') {
        // Verification mode: Call login with OTP code
        if (!loginCredentials) {
          setError('Login credentials not available. Please try logging in again.');
          setLoading(false);
          return;
        }

        console.log('🔐 Verifying OTP code during login...');
        const result = await loginWithCredentials(
          loginCredentials.unique_id,
          loginCredentials.password,
          loginCredentials.verification_id,
          code // OTP code
        );

        if (result.success) {
          console.log('✅ 2FA verification successful! Login completed.');
          setSuccess('Verification successful! Redirecting...');
          setStep('complete');
          setTimeout(() => navigate('/home/my-portal', { replace: true }), 2000);
        } else {
          // Check if it's a verification failure
          if ((result as any).code === '2FA_VERIFICATION_FAILED') {
            setError(result.error || 'Invalid verification code. Please try again.');
          } else {
            setError(result.error || 'Verification failed. Please try again.');
          }
        }
      } else {
        // Setup mode: Complete 2FA setup
        // For email OTP, setupData is not needed (no QR code/secret)
        // For TOTP, setupData is required (contains secret)
        if (otpMethod === 'totp' && !setupData) {
          setError('Setup data not available. Please refresh the page.');
          setLoading(false);
          return;
        }

        const storedSessionId = localStorage.getItem('2fa_session_id');

        if (storedSessionId) {
          // NEW FLOW: Use public endpoint with session_id
          console.log('🔐 Completing setup with session_id (public endpoint)');
          console.log('🔐 OTP Method:', otpMethod, 'Has setupData:', !!setupData);
          
          // For email OTP, we don't have a secret, so we pass empty string
          // For TOTP, we need the secret from setupData
          const secret = otpMethod === 'email' ? '' : (setupData?.secret || '');
          console.log('🔐 Sending to complete setup - secret:', secret ? '***' : '(empty for email)', 'code:', code, 'otpMethod:', otpMethod);
          
          const response = await completePublicTwoFactorSetup(
            storedSessionId, 
            secret, 
            code,
            otpMethod
          );

          if (response.success) {
            console.log('✅ 2FA setup completed successfully (NEW FLOW)!');

            // Store tokens from response
            if (response.access_token) {
              setStoredAccessToken(response.access_token);
            }
            if (response.refresh_token) {
              setStoredRefreshToken(response.refresh_token);
            }
            if (response.user) {
              localStorage.setItem('user', JSON.stringify(response.user));
            }

            // Clear temporary 2FA data
            localStorage.removeItem('2fa_session_id');
            localStorage.removeItem('2fa_user');

            setSuccess('2FA setup completed! Redirecting...');
            setStep('complete');
            setTimeout(() => {
              // Reload to ensure auth context picks up new tokens
              window.location.href = '/home/my-portal';
            }, 2000);
          }
        } else {
          // OLD FLOW: Use authenticated endpoint (only for TOTP, not email OTP)
          if (!setupData) {
            setError('Setup data not available. Please refresh the page.');
            setLoading(false);
            return;
          }
          
          console.log('🔐 Sending to /complete:', {
            secret: setupData.secret,
            code: code,
          });

          const response = await completeTwoFactorSetup(setupData.secret, code);

          if (response.success) {
            console.log('✅ 2FA setup completed successfully (OLD FLOW)!');
            setSuccess('2FA setup completed! Redirecting...');
            setStep('complete');
            setTimeout(() => navigate('/home/my-portal', { replace: true }), 2000);
          }
        }
      }
    } catch (err: any) {
      console.error('❌ 2FA verification/setup error:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {mode === 'verification' ? 'Loading...' : 'Setting up 2FA...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Setup Form */}
      <div className="w-full lg:w-[45%] lg:max-w-[45vw] flex flex-col justify-center px-8 sm:px-12 lg:px-24 xl:px-32 py-12">
        {/* Logo */}
        <div className="flex items-center mb-8">
          <span className="text-[#0061AF] font-bold text-4xl tracking-tight mr-1">Trainery</span>
          <div className="w-9 h-9 bg-[#FF4D4F] rounded-full flex items-center justify-center text-white font-bold text-2xl pb-1 shadow-sm">1</div>
          <span className="text-[#0061AF] text-[10px] font-bold self-start mt-1 ml-0.5">TM</span>
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <Shield className="w-8 h-8 text-blue-600" weight="fill" />
          <h1 className="text-3xl font-bold text-gray-900">
            {mode === 'verification' ? 'Two-Factor Verification' : 'Two-Factor Authentication'}
          </h1>
        </div>
        <p className="text-gray-500 text-base mb-8">
          {mode === 'verification' 
            ? (otpMethod === 'email' 
                ? `Enter the 6-digit verification code sent to your email${userEmail ? ` (${userEmail})` : ''}`
                : 'Enter the verification code from your authenticator app')
            : (otpMethod === 'email'
                ? 'Set up email-based two-factor authentication'
                : 'Enhance your account security with 2FA')
          }
        </p>

        {/* Messages */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        {/* Setup Mode: Show QR Code and Verification (TOTP only) */}
        {step === 'qr' && mode === 'setup' && otpMethod === 'totp' && (
          <div className="space-y-6">
            {/* QR Code Section */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Step 1: Scan QR Code</h2>

              {setupData ? (
                <div className="flex flex-col items-center">
                  {/* QR Code */}
                  <div className="bg-white p-6 rounded-lg mb-4">
                    <QRCodeSVG value={setupData.qrCode} size={256} level="H" />
                  </div>

                  {/* Manual Entry */}
                  <div className="w-full">
                    <p className="text-xs text-gray-500 mb-2 text-center">Manual entry code:</p>
                    <div className="bg-gray-100 px-4 py-3 rounded font-mono text-sm text-center break-all">
                      {setupData.secret}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  Failed to generate QR code. Please try again.
                </div>
              )}

              <div className="mt-4 space-y-2 text-sm text-gray-700">
                <p className="font-medium">Instructions:</p>
                <ol className="list-decimal list-inside space-y-1 pl-2">
                  <li>Open your authenticator app</li>
                  <li>Scan the QR code or enter the code manually</li>
                  <li>Enter the 6-digit code below</li>
                </ol>
              </div>
            </div>

            {/* Verification Section */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Step 2: Verify Code</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter 6-digit code
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest font-mono"
                    disabled={loading}
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter' && code.length === 6) {
                        handleVerifyCode();
                      }
                    }}
                  />
                </div>

                <Button
                  onClick={handleVerifyCode}
                  disabled={loading || code.length !== 6}
                  className="w-full"
                  size="lg"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </div>
                  ) : (
                    'Verify and Complete Setup'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Email OTP Setup Mode: Show Only OTP Input */}
        {step === 'verification' && mode === 'setup' && otpMethod === 'email' && (
          <div className="space-y-6">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Enter Verification Code</h2>
              <p className="text-sm text-gray-600 mb-6">
                {userEmail 
                  ? `We've sent a 6-digit verification code to ${userEmail}. Please check your email and enter the code below.`
                  : "We've sent a 6-digit verification code to your email. Please check your email and enter the code below."}
              </p>
              <p className="text-xs text-gray-500 mb-6">
                The code will expire in 5 minutes.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest font-mono"
                    disabled={loading}
                    autoFocus
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter' && code.length === 6) {
                        handleVerifyCode();
                      }
                    }}
                  />
                </div>

                <Button
                  onClick={handleVerifyCode}
                  disabled={loading || code.length !== 6}
                  className="w-full"
                  size="lg"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </div>
                  ) : (
                    'Verify and Complete Setup'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Verification Mode: Show Only OTP Input (No QR Code) */}
        {step === 'verification' && mode === 'verification' && (
          <div className="space-y-6">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Enter Verification Code</h2>
              <p className="text-sm text-gray-600 mb-6">
                {otpMethod === 'email'
                  ? (userEmail 
                      ? `Enter the 6-digit verification code sent to your email (${userEmail}).`
                      : 'Enter the 6-digit verification code sent to your email.')
                  : 'Open your authenticator app and enter the 6-digit code to complete your login.'}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest font-mono"
                    disabled={loading}
                    autoFocus
                    onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter' && code.length === 6) {
                        handleVerifyCode();
                      }
                    }}
                  />
                </div>

                <Button
                  onClick={handleVerifyCode}
                  disabled={loading || code.length !== 6}
                  className="w-full"
                  size="lg"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </div>
                  ) : (
                    'Verify and Sign In'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 'complete' && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" weight="fill" />
              <div>
                <h2 className="text-lg font-semibold text-green-900">Setup Complete!</h2>
                <p className="text-sm text-green-700">2FA is now active on your account</p>
              </div>
            </div>
          </div>
        )}

        {/* User Info */}
        {user && (
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Setting up 2FA for:</p>
            <p className="text-sm font-medium text-gray-900">{user.email}</p>
          </div>
        )}
      </div>

      {/* Right Side - Info */}
      <div className="hidden lg:flex lg:w-[55%] bg-gradient-to-br from-blue-50 to-blue-100 items-center justify-center p-12">
        <div className="max-w-md text-center">
          <Shield className="w-32 h-32 text-blue-600 mx-auto mb-6" weight="duotone" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Enhanced Security</h2>
          <p className="text-gray-700 mb-6">
            Two-factor authentication adds an extra layer of security with a verification code from your mobile device.
          </p>
          <div className="bg-white rounded-lg p-4 text-left">
            <p className="text-sm font-semibold text-gray-900 mb-2">Supported Apps:</p>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Google Authenticator</li>
              <li>• Microsoft Authenticator</li>
              <li>• Authy</li>
              <li>• Any TOTP app</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
